import { Box, CircularProgress } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";

import ModalDialog, { type DialogActionButtons } from "~/components/ModalDialog";
import OutlookAdminApprovalNotice from "~/components/settings/OutlookAdminApprovalNotice";
import { BodyTextSmall } from "~/components/Typographies";
import {
	useGetM365MailboxStatusQuery,
	useLazyGetM365MailboxAuthorizeUrlQuery
} from "~/gql/queries/m365Mailbox.generated";
import logger from "~/helpers/logger";
import m365Mailbox from "~/helpers/m365Mailbox";
import snackMessages from "~/helpers/snackMessages";
import useM365MailboxMutations from "~/hooks/mutations/useM365MailboxMutations";
import { useSnacks } from "~/providers/SnackProvider";

export type M365ConnectReason = "send-permission";

interface M365ConnectDialogProps {
	onClose: () => void;
	open: boolean;
	reason?: M365ConnectReason;
}

const M365ConnectDialog = ({ onClose, open, reason }: M365ConnectDialogProps) => {
	const { t } = useTranslation("fulfill");
	const [openSnack] = useSnacks();
	const { data, isError, isLoading, refetch } = useGetM365MailboxStatusQuery(undefined, { skip: !open });
	const [getAuthorizeUrl, { isFetching: connecting }] = useLazyGetM365MailboxAuthorizeUrlQuery();
	const { disconnectMailbox, isDisconnecting } = useM365MailboxMutations();

	const status = data?.m365MailboxStatus;

	const handleConnect = async () => {
		m365Mailbox.rememberReturnPath();
		try {
			const result = await getAuthorizeUrl({ redirectUri: m365Mailbox.redirectUri() }).unwrap();
			window.location.assign(result.m365MailboxAuthorizeUrl);
		} catch (error) {
			const message = snackMessages.actionError("starting Microsoft sign-in");
			logger.error(message, error);
			openSnack(message, "error");
		}
	};

	const handleDisconnect = async () => {
		const disconnected = await disconnectMailbox();
		if (disconnected) refetch();
	};

	const closeAction: DialogActionButtons = { children: "Close", props: { color: "inherit", onClick: onClose } };

	let body: React.ReactNode;
	let actions: DialogActionButtons[];

	if (isLoading) {
		body = (
			<Box sx={{ alignItems: "center", display: "flex", gap: 1, justifyContent: "center", py: 3 }}>
				<CircularProgress size={20} />
				<BodyTextSmall>Checking your mailbox connection...</BodyTextSmall>
			</Box>
		);
		actions = [closeAction];
	} else if (isError) {
		body = <BodyTextSmall>We couldn't check your mailbox connection. Please close this and try again.</BodyTextSmall>;
		actions = [closeAction];
	} else if (status?.connected && reason === "send-permission") {
		body = (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				<BodyTextSmall>
					<Trans
						components={{ bold: <strong /> }}
						i18nKey="orders.m365Connect.sendPermissionReconnect"
						ns="fulfill"
						values={{ mailboxAddress: status.mailboxAddress }}
					/>
				</BodyTextSmall>
				<OutlookAdminApprovalNotice />
			</Box>
		);
		actions = [
			{
				children: connecting ? "Reconnecting..." : "Reconnect",
				props: { disabled: connecting, onClick: handleConnect }
			},
			closeAction
		];
	} else if (status?.connected) {
		body = (
			<BodyTextSmall>
				<Trans
					components={{ bold: <strong /> }}
					i18nKey="orders.m365Connect.connected"
					ns="fulfill"
					values={{ mailboxAddress: status.mailboxAddress }}
				/>
			</BodyTextSmall>
		);
		actions = [
			{
				children: isDisconnecting ? "Disconnecting..." : "Disconnect",
				props: { color: "error", disabled: isDisconnecting, onClick: handleDisconnect }
			},
			closeAction
		];
	} else {
		body = (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				<BodyTextSmall>{t("orders.m365Connect.connectPrompt")}</BodyTextSmall>
				<OutlookAdminApprovalNotice />
			</Box>
		);
		actions = [
			{
				children: connecting ? "Connecting..." : "Connect mailbox",
				props: { disabled: connecting, onClick: handleConnect }
			},
			{ children: "Cancel", props: { color: "inherit", onClick: onClose } }
		];
	}

	return (
		<ModalDialog actions={actions} close={onClose} open={open} title="Connect your Microsoft mailbox">
			{body}
		</ModalDialog>
	);
};

export default M365ConnectDialog;
